// src/hooks/data/useAllGatherings.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { Gathering } from '../../../../types';
import gatheringDataService from '../../../../services/data/gatheringService';
import { getAllGatherings } from '../../../../services/data/selectors';

export const useAllGatherings = (token: string | null) => {
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const lastFocusTime = useRef<number>(0);
  
  // Set token in the data service
  useEffect(() => {
    gatheringDataService.setToken(token);
  }, [token]);
  
  // Load all data with SWR pattern
  const loadData = useCallback(async (background: boolean = false) => {
    if (!token) return;
    
    try {
      if (background) {
        setIsBackgroundLoading(true);
      } else {
        setIsInitialLoading(true);
      }
      
      await gatheringDataService.refreshAllData();
      setError(null);
    } catch (err) {
      setError('Failed to load gatherings');
      console.error('Error loading gatherings:', err);
    } finally {
      if (background) {
        setIsBackgroundLoading(false);
      } else {
        setIsInitialLoading(false);
      }
    }
  }, [token]);
  
  // Initial data loading
  useEffect(() => {
    if (!token) return;
    
    // Check if we have data
    const hasData = getAllGatherings().length > 0;
    
    if (!hasData) {
      // No data, do a normal load
      loadData(false);
    } else {
      // Data exists, no need to show loading state
      setIsInitialLoading(false);
    }
  }, [token, loadData]);
  
  // Set up revalidation triggers
  useEffect(() => {
    if (!token) return;
    
    // Revalidate on window focus
    const handleFocus = () => {
      const now = Date.now();
      // Debounce focus events to prevent multiple rapid reloads
      if (now - lastFocusTime.current > 5000) { // 5 second debounce
        lastFocusTime.current = now;
        loadData(true); // Important: use background loading
      }
    };
    
    // Revalidate on reconnect
    const handleOnline = () => {
      loadData(true); // Important: use background loading
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [token, loadData]);
  
  // Get all gatherings from the store
  const gatherings = getAllGatherings();
  
  return {
    gatherings,
    isLoading: isInitialLoading,
    isBackgroundLoading,
    error,
    refresh: () => loadData(false)
  };
};

export default useAllGatherings;